import React from 'react';
import { Typography } from 'antd';
import type { BaseFieldProps } from '../../../../types/form-renderer';
import type {
  VisualSearchSingleField as VisualSearchSingleFieldType,
  VisualSelectOption
} from '../../../../types/templates';
import { SearchGrid } from './shared/search-grid';
import { useImageUpload } from '../../../../hooks/media/use-image-upload';
import { UploadTile } from '../shared/upload-tile';

const { Text } = Typography;

type Props = BaseFieldProps<VisualSearchSingleFieldType>;

export const VisualSearchSingleField: React.FC<Props> = ({
  field,
  value,
  onChange,
  commonStyle,
  labelStyle,
  error
}) => {
  const selected = value ?? '';
  const baseOptions = React.useMemo(() => (Array.isArray(field.options) ? field.options : []), [field.options]);
  const { uploadImage, isUploading } = useImageUpload();

  const [uploadedUrls, setUploadedUrls] = React.useState<string[]>(() =>
    selected && !baseOptions.some(o => o.value === selected) ? [selected] : []
  );

  const options = React.useMemo<VisualSelectOption[]>(() => {
    if (uploadedUrls.length === 0) return baseOptions;
    const synthetic = uploadedUrls.map((url, i) => ({
      value: url,
      label: `Asset ${i + 1}`,
      imageUrl: url
    }));
    return [...synthetic, ...baseOptions];
  }, [baseOptions, uploadedUrls]);

  const handleCardClick = (optValue: string) => {
    onChange(selected === optValue ? '' : optValue);
  };

  const handleUpload = async (file: File) => {
    const url = await uploadImage(file);
    if (url) {
      setUploadedUrls(prev => (prev.includes(url) ? prev : [...prev, url]));
      onChange(url);
    }
  };

  const getSlotForValue = (val: string): 1 | 2 | null => (val === selected ? 1 : null);

  return (
    <div style={commonStyle}>
      <Text type='secondary' style={{ ...labelStyle, textTransform: 'uppercase' }}>
        {field.label}
      </Text>

      <SearchGrid
        options={options}
        placeholder={field.placeholder}
        getSlotForValue={getSlotForValue}
        showPip={false}
        onCardClick={handleCardClick}
        trailingCell={
          field.allowCustomInput ? (
            <UploadTile field={field} onUpload={handleUpload} isUploading={isUploading} width='100%' />
          ) : undefined
        }
      />

      {error && (
        <Text type='danger' style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
          {error}
        </Text>
      )}
    </div>
  );
};
